import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/core/services/auth.service';
import { StatistiqueService } from 'src/app/core/services/statistique.service';
import { CommandeService } from 'src/app/core/services/commande.service';
import { ManifesteService } from 'src/app/core/services/manifeste.service';

@Component({
  selector: 'app-dashboard-zone-manager',
  templateUrl: './dashboard-zone-manager.component.html',
  styleUrls: ['./dashboard-zone-manager.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DashboardZoneManagerComponent implements OnInit {

  utilisateur: any = null;
  zoneId: number | null = null;
  nomZone = '';
  zoneNonRenseignee = false;

  // Stats générales de la zone
  statsZone: any = null;
  chargementStats = true;

  // Intentions clients (commandes en attente)
  intentions: any[] = [];
  chargementIntentions = true;

  // Manifestes
  manifestes: any[] = [];
  chargementManifestes = true;

  // Commandes en cours (en transit / distribution)
  commandesEnCours: any[] = [];

  // Encaissements à valider
  encaissementsAValider: any[] = [];

  readonly STATUT_LIBELLE: Record<string, string> = {
    EN_ATTENTE: 'En attente',
    AFFECTE: 'Assigné',
    ENLEVEMENT_EN_COURS: 'Enlèvement',
    RECUPERE: 'Récupéré',
    AU_DEPOT: 'Au dépôt',
    EN_TRI: 'En tri',
    EN_TRANSIT: 'En transit',
    RECEPTIONNE: 'Réceptionné',
    EN_DISTRIBUTION: 'En distribution',
    EN_LIVRAISON: 'En livraison',
    LIVRE: 'Livré',
    INCIDENT: 'Incident',
    ANNULE: 'Annulé',
  };

  readonly STATUT_MANIFESTE_LIBELLE: Record<string, string> = {
    EN_PREPARATION: 'En préparation',
    CHARGE: 'Chargé',
    EXPEDIE: 'Expédié',
    EN_TRANSIT: 'En transit',
    RECEPTIONNE: 'Réceptionné',
    CLOTURE: 'Clôturé',
  };

  constructor(
    private authService: AuthService,
    private statistiqueService: StatistiqueService,
    private commandeService: CommandeService,
    private manifService: ManifesteService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.utilisateur = this.authService.obtenirUtilisateurCourant();
    this.zoneId = this.utilisateur?.zoneId ?? this.utilisateur?.zoneResponsableId ?? null;
    this.nomZone = this.extraireNomZoneDepuisUtilisateur(this.utilisateur);
    this.zoneNonRenseignee = !this.nomZone;

    // Le token peut ne pas contenir les infos zone; on enrichit depuis /users/current.
    this.authService.obtenirUtilisateur().subscribe({
      next: (user: any) => {
        if (!this.nomZone) {
          this.nomZone = this.extraireNomZoneDepuisUtilisateur(user);
          this.zoneNonRenseignee = !this.nomZone;
        }
        if (!this.zoneId) {
          this.zoneId = user?.zoneId ?? user?.zoneResponsableId ?? user?.zone?.id ?? null;
        }
      },
      error: () => {}
    });

    this.chargerStatsDashboard();
    this.chargerIntentions();
    this.chargerManifestes();
  }

  chargerStatsDashboard(): void {
    this.chargementStats = true;
    this.statistiqueService.obtenirDashboardZoneManager().subscribe({
      next: (data: any) => {
        this.statsZone = data;
        if (!this.nomZone) {
          if (typeof data?.zone === 'string' && data.zone.trim()) {
            this.nomZone = data.zone.trim();
          } else if (data?.zone?.nom) {
            this.nomZone = data.zone.nom;
          }
          this.zoneNonRenseignee = !this.nomZone;
        }
        if (!this.zoneId) {
          this.zoneId = data?.zoneId ?? data?.zone?.id ?? null;
        }
        this.commandesEnCours = data?.commandesEnCours ?? [];
        this.encaissementsAValider = data?.encaissementsNonValides ?? [];
        this.chargementStats = false;
      },
      error: () => { this.chargementStats = false; }
    });
  }

  chargerIntentions(): void {
    this.chargementIntentions = true;
    // Le backend auto-détecte la zone pour RESPONSABLE_ZONE (zoneId optionnel)
    this.commandeService.obtenirIntentionsClients(this.zoneId ?? undefined).subscribe({
      next: (data) => { this.intentions = data ?? []; this.chargementIntentions = false; },
      error: () => { this.chargementIntentions = false; }
    });
  }

  chargerManifestes(): void {
    this.chargementManifestes = true;
    this.manifService.listerManifestes(0, 10).subscribe({
      next: (data: any) => {
        this.manifestes = data?.content ?? [];
        this.chargementManifestes = false;
      },
      error: () => { this.chargementManifestes = false; }
    });
  }

  libelleStatut(s: string): string {
    return this.STATUT_LIBELLE[s] ?? s;
  }

  libelleStatutManifeste(s: string): string {
    return this.STATUT_MANIFESTE_LIBELLE[s] ?? s;
  }

  badgeStatut(s: string): string {
    const map: Record<string, string> = {
      EN_ATTENTE: 'badge--gris', AFFECTE: 'badge--bleu-clair',
      ENLEVEMENT_EN_COURS: 'badge--orange', RECUPERE: 'badge--jaune',
      AU_DEPOT: 'badge--violet', EN_TRI: 'badge--violet',
      EN_TRANSIT: 'badge--bleu', RECEPTIONNE: 'badge--teal',
      EN_DISTRIBUTION: 'badge--teal', EN_LIVRAISON: 'badge--vert-clair',
      LIVRE: 'badge--vert', INCIDENT: 'badge--rouge', ANNULE: 'badge--gris-fonce',
    };
    return map[s] ?? 'badge--gris';
  }

  badgeManifeste(s: string): string {
    const map: Record<string, string> = {
      EN_PREPARATION: 'badge--gris', CHARGE: 'badge--orange',
      EXPEDIE: 'badge--bleu', EN_TRANSIT: 'badge--bleu',
      RECEPTIONNE: 'badge--vert', CLOTURE: 'badge--gris-fonce',
    };
    return map[s] ?? 'badge--gris';
  }

  voirCommandeDetail(id: number): void {
    this.router.navigate(['/commande', id]);
  }

  voirToutesInterurbaines(): void {
    this.router.navigate(['/commande/interurbain']);
  }

  voirManifeste(id: number): void {
    // Navigation vers la page manifeste (si elle existe)
  }

  private extraireNomZoneDepuisUtilisateur(utilisateur: any): string {
    const candidats = [
      utilisateur?.zoneNom,
      utilisateur?.nomZone,
      utilisateur?.zoneResponsableNom,
      utilisateur?.zoneResponsable?.nom
    ];
    for (const valeur of candidats) {
      const texte = (valeur ?? '').toString().trim();
      if (texte) return texte;
    }
    return '';
  }
}
